/**
 * ChatChannelMember 类型和 Schema 定义
 *
 * ⚠️ 注意: 此文件由脚本自动生成，请勿手动修改
 * 如需更新，请运行代码生成脚本
 */
import type { SchemaDefinition } from './_schema'
import type { FileInfo } from './file_info'

/**
 * ChatChannelMember
 */
export interface ChatChannelMember {
  /** 创建时间（UTC） */
  created_at?: string
  /** 更新时间（UTC） */
  updated_at?: string
  /** 主键（UUID4），全局唯一，不可重复，36位 */
  id?: string
  /** 删除时间（UTC），goframe会自动处理deleted_at，如果想避免goframe自动处理，请使用Unscoped() */
  deleted_at?: string
  /** 删除原因 */
  deleted_reason?: string
  /** [S] 频道ID（不含类型前缀） */
  channel_id?: string
  /** [S] 频道类型 */
  channel_type?: string
  /** [S] 用户ID */
  user_id?: string
  /** [S] 频道角色（owner/admin/member） */
  role?: string
  /** [S] 置顶时间 */
  pinned_at?: string
  /** [S] 归档时间 */
  archived_at?: string
  /** [S] 是否免打扰 */
  notifications_muted?: boolean
  /** [S] 是否被禁 */
  banned?: boolean
  /** [S] 禁止过期时间 */
  ban_expires?: string
  /** [S] 是否影子封禁（用户不知道被禁） */
  shadow_banned?: boolean
  /** [S] 是否被邀请加入 */
  invited?: boolean
  /** [S] 接受邀请时间 */
  invite_accepted_at?: string
  /** [S] 拒绝邀请时间 */
  invite_rejected_at?: string
  /** [S] 扩展数据 */
  extra_data?: Record<string, any>
  /** [L] 最后已读时间 */
  last_read_at?: string
  /** [L] 未读总数 */
  unread_total_count?: number
  /** [L] 未读消息数 */
  unread_message_count?: number
  /** [L] 未读提及数 */
  unread_mention_count?: number
  /** [L] 聊天背景图片ID */
  background_id?: string
  /** [L] 聊天背景图片 */
  background?: FileInfo
  /** [L] 消息访问选项（0=全部，1=仅好友，2=禁止） */
  msg_access_option?: number
  /** [L] 本地草稿 */
  local_draft?: string
  /** [L] 草稿更新时间 */
  local_draft_at?: string
  /** [L] 加入时间 */
  joined_at?: string
  /** [L] 对线群玩家身份 */
  battle_player_identity?: string
  /** [L] 对线群阵营 */
  battle_side?: string
  /** [L] 群内昵称 */
  group_nickname?: string
  /** [L] 群备注名（仅自己可见） */
  group_remark?: string
  /** [L] 免打扰结束时间（临时免打扰） */
  muted_until?: string
  /** [L] Stream 同步状态 */
  stream_sync_status?: string
  /** [L] 同步错误 */
  stream_sync_error?: string
  /** [L] 同步时间 */
  stream_synced_at?: string
}

/**
 * ChatChannelMember Schema
 */
export const ChatChannelMemberSchema: SchemaDefinition = {
  properties: {
    created_at: {
      description: '创建时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '创建时间',
    },
    updated_at: {
      description: '更新时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '更新时间',
    },
    id: {
      description: '主键（UUID4），全局唯一，不可重复，36位',
      format: 'string',
      type: 'string',
      label: '主键',
    },
    deleted_at: {
      description:
        '删除时间（UTC），goframe会自动处理deleted_at，如果想避免goframe自动处理，请使用Unscoped()',
      format: '*time.Time',
      type: 'string',
      label: '删除时间',
    },
    deleted_reason: {
      description: '删除原因',
      format: 'string',
      type: 'string',
      label: '删除原因',
    },
    channel_id: {
      description: '[S] 频道ID（不含类型前缀）',
      format: 'string',
      type: 'string',
    },
    channel_type: {
      description: '[S] 频道类型',
      format: 'cons.ChatChannelType',
      type: 'string',
    },
    user_id: {
      description: '[S] 用户ID',
      format: 'string',
      type: 'string',
    },
    role: {
      description: '[S] 频道角色（owner/admin/member）',
      format: 'cons.ChatChannelMemberRole',
      type: 'string',
    },
    pinned_at: {
      description: '[S] 置顶时间',
      format: '*time.Time',
      type: 'string',
    },
    archived_at: {
      description: '[S] 归档时间',
      format: '*time.Time',
      type: 'string',
    },
    notifications_muted: {
      description: '[S] 是否免打扰',
      format: 'bool',
      type: 'boolean',
    },
    banned: {
      description: '[S] 是否被禁',
      format: 'bool',
      type: 'boolean',
    },
    ban_expires: {
      description: '[S] 禁止过期时间',
      format: '*time.Time',
      type: 'string',
    },
    shadow_banned: {
      description: '[S] 是否影子封禁（用户不知道被禁）',
      format: 'bool',
      type: 'boolean',
    },
    invited: {
      description: '[S] 是否被邀请加入',
      format: 'bool',
      type: 'boolean',
    },
    invite_accepted_at: {
      description: '[S] 接受邀请时间',
      format: '*time.Time',
      type: 'string',
    },
    invite_rejected_at: {
      description: '[S] 拒绝邀请时间',
      format: '*time.Time',
      type: 'string',
    },
    extra_data: {
      additionalProperties: { type: 'any', description: '' },
      description: '[S] 扩展数据',
      format: 'map[string]interface {}',
      type: 'object',
    },
    last_read_at: {
      description: '[L] 最后已读时间',
      format: '*time.Time',
      type: 'string',
    },
    unread_total_count: {
      description: '[L] 未读总数',
      format: 'int',
      type: 'integer',
    },
    unread_message_count: {
      description: '[L] 未读消息数',
      format: 'int',
      type: 'integer',
    },
    unread_mention_count: {
      description: '[L] 未读提及数',
      format: 'int',
      type: 'integer',
    },
    background_id: {
      description: '[L] 聊天背景图片ID',
      format: 'string',
      type: 'string',
    },
    background: { $ref: 'FileInfo', description: '[L] 聊天背景图片' },
    msg_access_option: {
      description: '[L] 消息访问选项（0=全部，1=仅好友，2=禁止）',
      format: 'int',
      type: 'integer',
    },
    local_draft: {
      description: '[L] 本地草稿',
      format: 'string',
      type: 'string',
    },
    local_draft_at: {
      description: '[L] 草稿更新时间',
      format: '*time.Time',
      type: 'string',
    },
    joined_at: {
      description: '[L] 加入时间',
      format: '*time.Time',
      type: 'string',
    },
    battle_player_identity: {
      description: '[L] 对线群玩家身份',
      format: 'cons.BattlePlayerIdentity',
      type: 'string',
    },
    battle_side: {
      description: '[L] 对线群阵营',
      format: 'cons.BattleSide',
      type: 'string',
    },
    group_nickname: {
      description: '[L] 群内昵称',
      format: 'string',
      type: 'string',
    },
    group_remark: {
      description: '[L] 群备注名（仅自己可见）',
      format: 'string',
      type: 'string',
    },
    muted_until: {
      description: '[L] 免打扰结束时间（临时免打扰）',
      format: '*time.Time',
      type: 'string',
    },
    stream_sync_status: {
      description: '[L] Stream 同步状态',
      format: 'cons.StreamSyncStatus',
      type: 'string',
    },
    stream_sync_error: {
      description: '[L] 同步错误',
      format: 'string',
      type: 'string',
    },
    stream_synced_at: {
      description: '[L] 同步时间',
      format: '*time.Time',
      type: 'string',
    },
  },
  type: 'object',
}
